import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../lib/api';
import { Icons } from '../components/icons';

type WechatForm = {
  enabled: boolean;
  mch_id: string;
  app_id: string;
  api_v3_key: string;
  mch_serial: string;
  private_key: string;
  api_v3_key_set?: boolean;
  api_v3_key_masked?: string;
  private_key_set?: boolean;
};

type AlipayForm = {
  enabled: boolean;
  app_id: string;
  gateway: string;
  private_key: string;
  public_key: string;
  private_key_set?: boolean;
  public_key_set?: boolean;
};

type BillingForm = {
  enabled: boolean;
  mock: boolean;
  public_base_url: string;
  order_ttl_minutes: number;
  wechat: WechatForm;
  alipay: AlipayForm;
};

const emptyForm = (): BillingForm => ({
  enabled: false,
  mock: true,
  public_base_url: '',
  order_ttl_minutes: 30,
  wechat: { enabled: true, mch_id: '', app_id: '', api_v3_key: '', mch_serial: '', private_key: '' },
  alipay: { enabled: true, app_id: '', gateway: 'https://openapi.alipay.com/gateway.do', private_key: '', public_key: '' },
});

export function BillingSettings() {
  const [form, setForm] = useState<BillingForm>(emptyForm());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    setMsg('');
    apiFetch('/api/v1/platform/billing-settings').then(d => {
      if (d.code === 0 && d.data) {
        const data = d.data as Record<string, any>;
        setForm({
          enabled: !!data.enabled,
          mock: !!data.mock,
          public_base_url: data.public_base_url || '',
          order_ttl_minutes: data.order_ttl_minutes || 30,
          wechat: {
            enabled: data.wechat?.enabled !== false,
            mch_id: data.wechat?.mch_id || '',
            app_id: data.wechat?.app_id || '',
            api_v3_key: '',
            mch_serial: data.wechat?.mch_serial || '',
            private_key: '',
            api_v3_key_set: data.wechat?.api_v3_key_set,
            api_v3_key_masked: data.wechat?.api_v3_key_masked,
            private_key_set: data.wechat?.private_key_set,
          },
          alipay: {
            enabled: data.alipay?.enabled !== false,
            app_id: data.alipay?.app_id || '',
            gateway: data.alipay?.gateway || 'https://openapi.alipay.com/gateway.do',
            private_key: '',
            public_key: '',
            private_key_set: data.alipay?.private_key_set,
            public_key_set: data.alipay?.public_key_set,
          },
        });
      } else {
        setMsg(d.message || '加载失败');
      }
      setLoading(false);
    }).catch(function(err) {
      setLoading(false);
      setMsg(err?.message || '加载失败，请确认 tenant 服务已更新');
    });
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (form.enabled && !form.mock && !form.public_base_url.trim()) {
      setMsg('启用真实支付时请填写网关公网地址（支付回调 URL 前缀）');
      return;
    }
    setSaving(true);
    setMsg('');
    const body = {
      enabled: form.enabled,
      mock: form.mock,
      public_base_url: form.public_base_url.trim(),
      order_ttl_minutes: parseInt(String(form.order_ttl_minutes), 10) || 30,
      wechat: {
        enabled: form.wechat.enabled,
        mch_id: form.wechat.mch_id.trim(),
        app_id: form.wechat.app_id.trim(),
        api_v3_key: form.wechat.api_v3_key.trim(),
        mch_serial: form.wechat.mch_serial.trim(),
        private_key: form.wechat.private_key.trim(),
      },
      alipay: {
        enabled: form.alipay.enabled,
        app_id: form.alipay.app_id.trim(),
        gateway: form.alipay.gateway.trim() || 'https://openapi.alipay.com/gateway.do',
        private_key: form.alipay.private_key.trim(),
        public_key: form.alipay.public_key.trim(),
      },
    };
    const d = await apiFetch('/api/v1/platform/billing-settings', { method: 'PUT', body });
    setSaving(false);
    if (d.code === 0) {
      setMsg('支付配置已保存');
      load();
    } else {
      setMsg(d.message || '保存失败');
    }
  };

  const setWechat = (patch: Partial<WechatForm>) => setForm(f => ({ ...f, wechat: { ...f.wechat, ...patch } }));
  const setAlipay = (patch: Partial<AlipayForm>) => setForm(f => ({ ...f, alipay: { ...f.alipay, ...patch } }));

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 650 }}>订阅计费 · 支付商户</h2>
          <p style={{ fontSize: 13, color: 'var(--brand-text-muted)', marginTop: 4 }}>
            配置微信支付、支付宝商户参数。密钥保存在平台数据库中，界面不回显完整内容。
          </p>
        </div>
        <button className="btn btn-outline" onClick={load} disabled={loading}><Icons.Refresh/>刷新</button>
      </div>

      {loading ? <div className="empty"><span className="spinner"/></div> : (
        <div style={{ display: 'grid', gap: 16, maxWidth: 720 }}>
          <div className="card">
            <div className="card-title">全局</div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, cursor: 'pointer' }}>
              <input type="checkbox" checked={form.enabled} onChange={e => setForm(f => ({ ...f, enabled: e.target.checked }))}/>
              启用在线支付（租户须付费转正，关闭免费「试用转正式」）
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, cursor: 'pointer' }}>
              <input type="checkbox" checked={form.mock} onChange={e => setForm(f => ({ ...f, mock: e.target.checked }))}/>
              模拟支付模式（联调用，不调用真实微信/支付宝 API）
            </label>
            <div className="form-group">
              <label>网关公网地址</label>
              <input className="form-input" value={form.public_base_url} onChange={e => setForm(f => ({ ...f, public_base_url: e.target.value }))} placeholder="https://api.yourdomain.com"/>
              <div style={{ fontSize: 11, color: 'var(--brand-text-muted)', marginTop: 4 }}>
                用于拼接回调：/api/v1/webhooks/wechat-pay、/api/v1/webhooks/alipay
              </div>
            </div>
            <div className="form-group">
              <label>订单有效期（分钟）</label>
              <input className="form-input" type="number" value={form.order_ttl_minutes} onChange={e => setForm(f => ({ ...f, order_ttl_minutes: Number(e.target.value) }))}/>
            </div>
          </div>

          <div className="card">
            <div className="card-title">微信支付（Native 扫码）</div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, cursor: 'pointer' }}>
              <input type="checkbox" checked={form.wechat.enabled} onChange={e => setWechat({ enabled: e.target.checked })}/>
              启用微信支付
            </label>
            <div className="form-group">
              <label>商户号 mch_id</label>
              <input className="form-input" value={form.wechat.mch_id} onChange={e => setWechat({ mch_id: e.target.value })}/>
            </div>
            <div className="form-group">
              <label>AppID</label>
              <input className="form-input" value={form.wechat.app_id} onChange={e => setWechat({ app_id: e.target.value })}/>
            </div>
            <div className="form-group">
              <label>API v3 密钥</label>
              <input className="form-input" type="password" value={form.wechat.api_v3_key} onChange={e => setWechat({ api_v3_key: e.target.value })} placeholder={form.wechat.api_v3_key_set ? `已设置 ${form.wechat.api_v3_key_masked || ''}` : '32 位 APIv3 密钥'}/>
            </div>
            <div className="form-group">
              <label>商户证书序列号</label>
              <input className="form-input" value={form.wechat.mch_serial} onChange={e => setWechat({ mch_serial: e.target.value })}/>
            </div>
            <div className="form-group">
              <label>商户私钥（PEM）</label>
              <textarea className="form-input" rows={4} value={form.wechat.private_key} onChange={e => setWechat({ private_key: e.target.value })} placeholder={form.wechat.private_key_set ? '已设置，留空则保持不变' : '-----BEGIN PRIVATE KEY-----'} style={{ fontFamily: 'monospace', fontSize: 12 }}/>
            </div>
          </div>

          <div className="card">
            <div className="card-title">支付宝（当面付 precreate）</div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, cursor: 'pointer' }}>
              <input type="checkbox" checked={form.alipay.enabled} onChange={e => setAlipay({ enabled: e.target.checked })}/>
              启用支付宝
            </label>
            <div className="form-group">
              <label>AppID</label>
              <input className="form-input" value={form.alipay.app_id} onChange={e => setAlipay({ app_id: e.target.value })}/>
            </div>
            <div className="form-group">
              <label>网关地址</label>
              <input className="form-input" value={form.alipay.gateway} onChange={e => setAlipay({ gateway: e.target.value })}/>
            </div>
            <div className="form-group">
              <label>应用私钥（PEM）</label>
              <textarea className="form-input" rows={4} value={form.alipay.private_key} onChange={e => setAlipay({ private_key: e.target.value })} placeholder={form.alipay.private_key_set ? '已设置，留空则保持不变' : '应用 RSA2 私钥'} style={{ fontFamily: 'monospace', fontSize: 12 }}/>
            </div>
            <div className="form-group">
              <label>支付宝公钥（PEM）</label>
              <textarea className="form-input" rows={4} value={form.alipay.public_key} onChange={e => setAlipay({ public_key: e.target.value })} placeholder={form.alipay.public_key_set ? '已设置，留空则保持不变' : '用于验签回调'} style={{ fontFamily: 'monospace', fontSize: 12 }}/>
            </div>
          </div>

          {msg && <div className={msg.includes('失败') ? 'error' : 'lc-badge'}>{msg}</div>}
          <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? '保存中…' : '保存支付配置'}</button>
        </div>
      )}
    </div>
  );
}
